import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { FeatureIcon } from '@rossum/ui';
import { Link, Stack, Typography } from '@rossum/ui/material';
import { isEmbedded } from '../../../../constants/config';
import { isUserAdmin } from '../../../../redux/modules/user/selectors';
import { extensionDetailPath } from '../../../extensions/routes';
import { FEATURE_ICON_GAP } from '../constants';
import { formatActivityTimestamp } from '../helpers/formatActivityTimestamp';
import { getActorName } from '../helpers/getActorName';
import { toFeatureIcon } from '../helpers/sourceIcon';
import type { ActivityBlock, ActivitySource } from '../model';
import { ActivityBlockMenu } from './ActivityBlockMenu';

const ActorTitle = ({ source }: { source: ActivitySource }) => {
  const intl = useIntl();
  const isAdmin = useSelector(isUserAdmin);

  if (source.sourceType !== 'hook' || source.actor === null) {
    return <>{getActorName(intl, source)}</>;
  }

  // Only admins can open the extension detail.
  const { hookId } = source;
  return (
    <>
      {intl.formatMessage(
        { id: 'features.documentHistory.actor.hookNamed' },
        {
          name: source.actor,
          link:
            hookId && isAdmin && !isEmbedded()
              ? (chunks: ReactNode[]) => (
                  <Link
                    component={RouterLink}
                    to={extensionDetailPath(hookId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                )
              : (chunks: ReactNode[]) => <>{chunks}</>,
        }
      )}
    </>
  );
};

export const ActivityBlockHeader = ({ block }: { block: ActivityBlock }) => {
  const intl = useIntl();
  const actor = getActorName(intl, block.source);

  return (
    <Stack
      direction="row"
      spacing={FEATURE_ICON_GAP}
      useFlexGap
      sx={{ alignItems: 'center' }}
      data-cy="document-history-block-header"
    >
      <FeatureIcon {...toFeatureIcon(block.source, actor)} size="small" />
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flex: 1, minWidth: 0, alignItems: 'baseline' }}
      >
        <Typography
          variant="h6"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <ActorTitle source={block.source} />
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          {formatActivityTimestamp(intl, block.timestamp)}
        </Typography>
      </Stack>
      <ActivityBlockMenu requestIds={block.requestIds} />
    </Stack>
  );
};
